import { auth, firestore } from "./config";
import { httpsCallable } from "firebase/functions";
import { getClientExams, getActivationDataURL, getCompleteActivationURL } from "./client";
import { BUNDLE_APP_NAME } from '../../app_constant';
import { serverTimestamp, Timestamp } from "firebase/firestore";
import { UPDATE_BUNDLE_USER_PROFILE } from "./firebase_user";
import { AuthContext } from "../context/AuthProvider";
import { createLogger } from "../utils/logger";

const logger = createLogger(process.env.REACT_APP_ENV === 'DEVELOPMENT', 'Activation');

const DEBUG_KEY = 'activation_debug_logs';
export const LICENSE_STATUS = {
  ASSIGNED: 'assigned',
  ACTIVATED: 'activated',
  NOT_SENT: 'invite_not_sent',
}


// Helper function to make HTTP POST requests
async function makeHttpRequest(url, data) {
  
  try {
    // Ensure URL is a string and not a Promise
    const requestUrl = typeof url === 'string' ? url : await url;
    
    logger.log('Making HTTP request to:', requestUrl);
    logger.log('With data:', JSON.stringify(data, null, 2));

    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Origin': window.location.origin
      },
      mode: 'cors',
      credentials: 'omit',
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('HTTP error response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
        headers: Object.fromEntries(response.headers.entries())
      });
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    const responseData = await response.json();
    logger.log('HTTP response:', JSON.stringify(responseData, null, 2));
    return responseData;
  } catch (error) {
    logger.error('HTTP request failed:', error);
    throw error;
  }
}



export async function FETCH_BUNDLE_DETAILS(uid) {
    const bundleAppName = await BUNDLE_APP_NAME;
    const authRef = firestore()
        .collection("apps")
        .doc(bundleAppName)
        .collection("users");

    try {
        const doc = await authRef.doc(uid).get();

        if (doc.exists) {
            return doc.data();
        } else {
            return null;
        }

    } catch (error) {
        logger.error("Error fetching bundle details:", error);
    }
}


async function STORE_AUTH_DETAILS(APP_NAME, email, uid) {
    const authRef = firestore()
        .collection("apps")
        .doc("AUTH")
        .collection("users");

    try {
        if (!uid) {
            logger.error("Invalid UID");
            return;
        }

        await authRef.doc(uid).set({
            email: email,
            apps: [APP_NAME]
        });

        logger.log('Auth details saved successfully');
    } catch (error) {
        logger.error("Error storing auth details:", error);
    }
}



export const verifyInvite = async (clientId, activation_code) => {
  try {
    const response = await makeHttpRequest(
      getActivationDataURL(),
      { clientId, activationCode: activation_code }
    );
    return response.success;
  } catch (error) {
    logger.error("verifyInvite error:", error);
    return false;
  }
};



// Helper function to create or check Firebase Auth user
async function createOrCheckFirebaseUser(user_id, pwd, firstName, lastName) {
  logger.log('Checking if user already exists');
  logger.log(`Checking auth methods for email: ${user_id}`);
  
  const methods = await auth().fetchSignInMethodsForEmail(user_id);
  if (methods.length > 0) {
    logger.log('User already exists');
    logger.log(`User with email ${user_id} already exists with auth methods:`, methods);
    return {
      success: false,
      message: "An account with this email already exists. Please sign in to continue."
    };
  }

  let uid;
  try {
    logger.log('Creating new user account');
    logger.log(`Creating Firebase Auth user with email: ${user_id}`);
    
    const userCred = await auth().createUserWithEmailAndPassword(user_id, pwd);
    uid = userCred.user.uid;
    
    logger.log(`User created with UID: ${uid}`);
    logger.log(`Updating user profile with name: ${firstName} ${lastName}`);
    
    await userCred.user.updateProfile({ displayName: `${firstName} ${lastName}` });
    logger.log('Created user with details:', {
      uid,
      email: user_id,
      displayName: `${firstName} ${lastName}`
    });
    
    return { success: true, uid };
  } catch (err) {
    logger.log('Error creating user');
    logger.error('Firebase Auth error:', err);
    logger.log('Error details:', {
      code: err.code,
      message: err.message,
      stack: err.stack
    });
    return { success: false, message: "This email is already registered. Please sign in instead or use a different email address." };
  }
}



// Helper function to check existing app-user document
async function checkExistingAppUser(appCollectionName, uid, examTitle) {
  logger.log('Checking existing app-user document');
  logger.log(`Querying Firestore: apps/${appCollectionName}/users/${uid}`);
  
  const appUserRef = firestore()
    .collection("apps").doc(appCollectionName)
    .collection("users").doc(uid);
  const appUserSnap = await appUserRef.get();
  const now = Timestamp.now();

  if (appUserSnap.exists) {
    const appUserData = appUserSnap.data();
    const { subscription_expiration } = appUserData.subscription || {};
    
    logger.log('Found existing app-user document:', {
      uid,
      auth_email: appUserData.auth_email,
      subscription_expiration: subscription_expiration?.toDate()
    });
    
    if (subscription_expiration?.toDate() > new Date()) {
      logger.log('User already has an active subscription');
      logger.log(`Subscription active until: ${subscription_expiration.toDate()}`);
      return {
        success: false,
        message: `You already have access to ${examTitle}. Please sign in to continue.`
      };
    }
    logger.log('Found existing user document with expired subscription');
  } else {
    logger.log('No existing user document found');
  }
  
  return { success: true, appUserRef, appUserSnap, now };
}



// Helper function to create/update app-user document
async function createOrUpdateAppUser(appCollectionName, uid, user_id, firstName, lastName, startDate, endDate, clientId, licenseDoc, planId, examId) {
  logger.log('Creating/updating app-user document');
  logger.log(`Writing to: apps/${appCollectionName}/users/${uid}`);
  
  const userDocData = {
    auth_email: user_id,
    firstName,
    lastName,
    subscriptions: {
      [examId.toLowerCase()]: {
        created: serverTimestamp(),
        subscription_start: startDate,
        subscription_expiration: endDate,
        clientId: clientId,
        licenseId: licenseDoc.id,
        planId: planId,
        examId: examId
      }
    }
  };
  
  logger.log('User document data to be written:', userDocData);

  const appUserRef = firestore()
    .collection("apps")
    .doc(appCollectionName)
    .collection("users")
    .doc(uid);

  const appUserSnap = await appUserRef.get();

  if (appUserSnap.exists) {
    logger.log('Updating existing app-user document');
    await appUserRef.update(userDocData);
  } else {
    logger.log('Creating new app-user document');
    await appUserRef.set(userDocData);
  }
  logger.log('App-user document saved successfully');
  
  return { success: true };
}

// Helper function to create bundle profile
async function createBundleProfile(clientId, startDate, endDate, licenseDoc, planId, examId, firstName, lastName) {
  logger.log('=== START createBundleProfile ===', { clientId, startDate, endDate, licenseDoc, planId, examId });
  
  try {
    logger.log('Calling getClientExams with clientId:', clientId);
    const clientExams = await getClientExams(clientId);
    logger.log('getClientExams result:', clientExams);
    
    if (!clientExams) {
      logger.log('clientExams is null/undefined', null);
      throw new Error(`No exams found for client ID: ${clientId}`);
    }
    
    if (clientExams.length === 0) {
      logger.log('clientExams array is empty', null);
      throw new Error(`No exams found for client ID: ${clientId}`);
    }
    
    logger.log('First exam details:', {
      key: clientExams[0]?.key,
      title: clientExams[0]?.title,
      id: clientExams[0]?.id
    });
    
    const currentCertificate = clientExams.find(exam => exam.title === examId)?.key;
    logger.log('Setting current_certificate to:', currentCertificate);
    
    const data = {
      auth_first_name: firstName,
      auth_last_name: lastName,
      auth_email: auth().currentUser.email,
      last_signin_time: auth().currentUser.metadata?.lastSignInTime ?? "",
      signup_date: auth().currentUser.metadata?.creationTime ?? "",
      current_certificate: currentCertificate || 'Exam Not Found',
      subscriptions: {
        [currentCertificate]: {
          created: serverTimestamp(),
          subscription_start: startDate,
          subscription_expiration: endDate,
          clientId: clientId,
          licenseId: licenseDoc.id,
          planId: planId,
          examId: examId
        }
      }
    };
    
    logger.log('Final data object:', data);
    logger.log('Calling UPDATE_BUNDLE_USER_PROFILE', null);
    
    try {
      await UPDATE_BUNDLE_USER_PROFILE(data);
      logger.log('UPDATE_BUNDLE_USER_PROFILE completed successfully', null);
      return { success: true };
    } catch (error) {
      logger.log('Error in UPDATE_BUNDLE_USER_PROFILE:', error);
      return { success: false, error };
    }
  } catch (error) {
    logger.log('Error in createBundleProfile:', error);
    throw new Error(`Failed to create bundle profile: ${error.message}`);
  } finally {
    logger.log('=== END createBundleProfile ===', null);
  }
}

export const acceptInvite = async ({
  clientId,
  activation_code,
  user_id,
  pwd,
  firstName,
  lastName
}) => {
  logger.log('=== ACTIVATION PROCESS STARTED ===', { clientId, activation_code, user_id });

  try {
    // 1) Fetch activation payload from client project
    const prepRes = await makeHttpRequest(
      getActivationDataURL(),
      { clientId, activationCode: activation_code }
    );
    if (!prepRes.success) return prepRes;
    const { licenseId, planId, examId, numberOfDays, appCollectionName, examTitle } = prepRes.payload;

    // 2) Ensure Auth user exists
    const userResult = await createOrCheckFirebaseUser(user_id, pwd, firstName, lastName);
    if (!userResult.success) return userResult;
    const { uid } = userResult;

    // 3) Check local app-user record
    const appUserResult = await checkExistingAppUser(appCollectionName, uid, examTitle);
    if (!appUserResult.success) return appUserResult;
    const { appUserRef, appUserSnap } = appUserResult;

    // 4) Compute subscription window
    const startMs = Date.now();
    const endMs   = startMs + numberOfDays * 24 * 60 * 60 * 1000;

    // 5) Complete activation in client project
    const completeData = {
      clientId,
      licenseId,
      startMillis: startMs,
      endMillis: endMs,
      userEmail: user_id
    };
    logger.log('Sending complete activation data:', JSON.stringify(completeData, null, 2));
    
    const completeRes = await makeHttpRequest(
      getCompleteActivationURL(),
      completeData
    );
    if (!completeRes.success) {
      logger.error('Complete activation failed:', completeRes);
      return completeRes;
    }

    // 6) Update or create the local app-user document
    await createOrUpdateAppUser(
      appCollectionName,
      uid,
      user_id,
      firstName,
      lastName,
      Timestamp.fromMillis(startMs),
      Timestamp.fromMillis(endMs),
      clientId,
      { id: licenseId },
      planId,
      examId
    );

    // 7) Create or update bundle profile
    try {
      await createBundleProfile(
        clientId,
        Timestamp.fromMillis(startMs),
        Timestamp.fromMillis(endMs),
        { id: licenseId },
        planId,
        examId,
        firstName,
        lastName
      );
    } catch (error) {
      logger.error('Error creating bundle profile:', error);
      return { success: false, message: `Bundle profile failed: ${error.message}` };
    }

    return { success: true, message: "Account activated successfully." };
  } catch (error) {
    logger.error('Error in acceptInvite:', error);
    return { success: false, message: `Activation failed: ${error.message}` };
  }
};

export const reactivateUser = async ({
  clientId,
  activation_code
}) => {
  logger.log('=== REACTIVATION PROCESS STARTED ===', { clientId, activation_code });

  try {
    // 1) Fetch activation payload from client project
    const prepRes = await makeHttpRequest(
      getActivationDataURL(),
      { clientId, activationCode: activation_code }
    );
    if (!prepRes.success) return prepRes;
    const { licenseId, planId, examId, numberOfDays, appCollectionName, examTitle } = prepRes.payload;

    // 2) Get current user
    const currentUser = auth().currentUser;
    if (!currentUser) {
      return { 
        success: false, 
        message: "User authentication error. Please sign in again." 
      };
    }
    const uid = currentUser.uid;
    const userEmail = currentUser.email;

    // 3) Check if user exists in app collection
    const bundleAppName = await BUNDLE_APP_NAME;
    const bundleUserRef = firestore()
      .collection("apps")
      .doc(bundleAppName)
      .collection("users")
      .doc(uid);
    
    const bundleUserSnap = await bundleUserRef.get();
    if (!bundleUserSnap.exists) {
      return { 
        success: false, 
        message: "User profile not found. Please contact support." 
      };
    }

    // Get the exam key from clientExams
    const clientExams = await getClientExams(clientId);
    if (!clientExams || clientExams.length === 0) {
      return {
        success: false,
        message: `No exams found for client ID: ${clientId}`
      };
    }
    
    const examKey = clientExams.find(exam => exam.title === examId)?.key;
    if (!examKey) {
      return {
        success: false,
        message: `No matching exam found for ID: ${examId}`
      };
    }

    // 4) Compute new subscription window
    const startMs = Date.now();
    const endMs = startMs + numberOfDays * 24 * 60 * 60 * 1000;

    // 5) Complete activation in client project
    const completeData = {
      clientId,
      licenseId,
      startMillis: startMs,
      endMillis: endMs,
      userEmail
    };
    logger.log('Sending complete reactivation data:', JSON.stringify(completeData, null, 2));
    
    const completeRes = await makeHttpRequest(
      getCompleteActivationURL(),
      completeData
    );
    if (!completeRes.success) {
      logger.error('Complete reactivation failed:', completeRes);
      return completeRes;
    }

    // 6) Update the bundle user document with new subscription
    const userDocData = {
      subscriptions: {
        [examKey]: {
          created: serverTimestamp(),
          subscription_start: Timestamp.fromMillis(startMs),
          subscription_expiration: Timestamp.fromMillis(endMs),
          clientId: clientId,
          licenseId: licenseId,
          planId: planId,
          examId: examId
        }
      }
    };

    await bundleUserRef.update(userDocData);

    return { 
      success: true, 
      message: "Account reactivated successfully." 
    };
  } catch (error) {
    logger.error('Error in reactivateUser:', error);
    return { 
      success: false, 
      message: `Reactivation failed: ${error.message}` 
    };
  }
};